import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from './Button.jsx';

const navLinks = [
  { 
    label: 'Les produits', 
    href: '/products',
    dropdown: [
      { label: 'Pains', href: '/products/pains' },
      { label: 'Pâtisserie', href: '/products/patisserie' },
      { label: 'Boissons', href: '/products/boissons' },
      { label: 'Spécialités', href: '/products/specialites' }
    ]
  },
  { label: 'À propos', href: '/about' },
  { label: 'Notre héritage', href: '/heritage' },
  { label: 'Nos engagements', href: '/commitments' },
  { label: 'Devenir franchisé', href: '/franchise' },
  { label: 'Services', href: '/services' }
];

export function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const location = useLocation();

  const isActiveLink = (href) => {
    return location.pathname === href || (href !== '/' && location.pathname.startsWith(href));
  };

  return (
    <header className="fixed top-0 left-0 w-full z-50 bg-surface/80 backdrop-blur-lg border-b border-border transition-all duration-300 shadow-md">
      <div className="container mx-auto flex items-center justify-between px-4 py-3">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2" aria-label="Antic Bakery Home">
          <span className="font-heading text-2xl font-extrabold tracking-tight text-primary">Antic</span>
        </Link>
        {/* Desktop Nav */}
        <nav className="hidden md:flex gap-8 items-center" aria-label="Main navigation">
          {navLinks.map((link, idx) => (
            <div key={link.label} className="relative group">
              <Link
                to={link.href}
                className={`px-2 py-1 font-medium transition-colors duration-200 hover:text-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-accent rounded ${
                  isActiveLink(link.href) ? 'text-accent' : 'text-text-primary'
                }`}
                aria-haspopup={!!link.dropdown}
                aria-expanded={activeDropdown === idx}
                onMouseEnter={() => setActiveDropdown(idx)}
                onMouseLeave={() => setActiveDropdown(null)}
                onFocus={() => setActiveDropdown(idx)}
                onBlur={() => setActiveDropdown(null)}
              >
                {link.label}
              </Link>
              {link.dropdown && activeDropdown === idx && (
                <div className="absolute left-0 mt-2 bg-surface border border-border rounded-lg shadow-lg py-2 min-w-[180px] animate-fade-in z-50">
                  {link.dropdown.map((item) => (
                    <Link
                      key={item.label}
                      to={item.href}
                      className="block px-4 py-2 text-text-primary hover:bg-accent/10 hover:text-accent transition-colors duration-150"
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ))}
        </nav>
        {/* Utility Nav */}
        <div className="flex items-center gap-3">
          <button aria-label="Search" className="p-2 rounded-full hover:bg-accent/10 focus-visible:ring-2 focus-visible:ring-accent">
            <span className="material-symbols-outlined">search</span>
          </button>
          <button aria-label="Store locator" className="p-2 rounded-full hover:bg-accent/10 focus-visible:ring-2 focus-visible:ring-accent">
            <span className="material-symbols-outlined">location_on</span>
          </button>
          <button aria-label="User login" className="p-2 rounded-full hover:bg-accent/10 focus-visible:ring-2 focus-visible:ring-accent">
            <span className="material-symbols-outlined">person</span>
          </button>
          <button aria-label="Cart" className="p-2 rounded-full hover:bg-accent/10 focus-visible:ring-2 focus-visible:ring-accent">
            <span className="material-symbols-outlined">shopping_bag</span>
          </button>
          {/* Hamburger for mobile */}
          <button
            className="md:hidden ml-2 p-2 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            aria-label="Open menu"
            aria-expanded={mobileOpen}
            onClick={() => setMobileOpen((v) => !v)}
          >
            <span className="material-symbols-outlined text-2xl">{mobileOpen ? 'close' : 'menu'}</span>
          </button>
        </div>
      </div>
      {/* Mobile Nav */}
      {mobileOpen && (
        <nav className="md:hidden bg-surface border-t border-border shadow-lg animate-fade-in" aria-label="Mobile navigation">
          <ul className="flex flex-col gap-2 p-4">
            {navLinks.map((link) => (
              <li key={link.label}>
                <Link 
                  to={link.href} 
                  className={`block px-4 py-2 rounded font-medium transition-colors duration-150 hover:bg-accent/10 hover:text-accent ${
                    isActiveLink(link.href) ? 'text-accent bg-accent/10' : 'text-text-primary'
                  }`}
                  onClick={() => setMobileOpen(false)}
                >
                  {link.label}
                </Link>
                {link.dropdown && (
                  <ul className="pl-4 mt-1">
                    {link.dropdown.map((item) => (
                      <li key={item.label}>
                        <Link 
                          to={item.href} 
                          className="block px-4 py-1 text-text-secondary hover:text-accent"
                          onClick={() => setMobileOpen(false)}
                        >
                          {item.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
            {/* Developer link to theme switcher */}
            <li className="border-t border-border mt-2 pt-2">
              <Link 
                to="/themes" 
                className="block px-4 py-2 rounded text-text-secondary hover:bg-accent/10 hover:text-accent font-medium transition-colors duration-150 text-sm"
                onClick={() => setMobileOpen(false)}
              >
                🎨 Theme Switcher
              </Link>
            </li>
          </ul>
        </nav>
      )}
    </header>
  );
}

export default Header;
